import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismDrawer, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralButtonsButtonExpandArrow from 'lumen/components/general/buttons/button-expand-arrow';
import SettingsMappingDefinitionsDefinitionContent from 'lumen/components/settings/mapping/definitions/definition-content';
import isDrawerExpanded from 'lumen/helpers/is-drawer-expanded';
import { DEFINITIONS } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { HEADER_HEIGHT } from 'lumen/utils/constants/general-constants';

const DRAWER_ID = 'mapping-definition-drawer';

interface SettingsMappingDefinitionsMappingDefinitionsSignature {
  Args: {};
}

export default class SettingsMappingDefinitionsMappingDefinitions extends Component<SettingsMappingDefinitionsMappingDefinitionsSignature> {
  @service declare drawer: Services['drawer'];

  @tracked expandedDefinitions = Object.entries(DEFINITIONS).reduce(
    (acc, next) => {
      return {
        ...acc,
        [next[1].name]: false,
      };
    },
    {},
  );

  constructor(
    owner: Owner,
    args: SettingsMappingDefinitionsMappingDefinitionsSignature['Args'],
  ) {
    super(owner, args);

    // @ts-expect-error: Incorrect type
    this.drawer.registerDrawer(DRAWER_ID);
  }

  @action onToggleDrawer(): void {
    // @ts-expect-error: Incorrect type
    this.drawer.toggleDrawerState(DRAWER_ID);
  }

  @action onToggleExpansion(categoryName: string): void {
    this.expandedDefinitions = {
      ...this.expandedDefinitions,
      // @ts-expect-error: Incorrect type
      [categoryName]: !this.expandedDefinitions[categoryName],
    };
  }

  <template>
    <PrismDrawer
      @contentPadding="none"
      {{! @glint-expect-error: Incorrect type }}
      @isOpen={{isDrawerExpanded DRAWER_ID}}
      @onToggle={{this.onToggleDrawer}}
      @positionTop={{HEADER_HEIGHT}}
    >
      <:trigger>
        <div
          class="definition-drawer-trigger-icon flex items-center justify-center"
        >
          <PrismIcon
            @color="default-inverse"
            @name="circle-information-outline"
            @size="large"
          />
        </div>
      </:trigger>
      <:content>
        <div class="definition-drawer">
          <div class="definition-drawer__header">
            <div class="mb-400 flex flex-row items-center justify-between">
              <h4 class="text-bold">{{t
                  "benchmark_insight.definition.header"
                }}</h4>

              <button
                class="button--unstyled"
                type="button"
                {{on "click" this.onToggleDrawer}}
              >
                <PrismIcon @name="cross" @size="small" />
              </button>
            </div>
          </div>

          <div class="definition-drawer__content">
            {{#each-in DEFINITIONS as |category definition|}}
              <div class="definition-item paragraph-200--regular">
                <button
                  class="button--unstyled definition-item__name_container flex flex-row items-center"
                  type="button"
                  {{on "click" (fn this.onToggleExpansion definition.name)}}
                >
                  <GeneralButtonsButtonExpandArrow
                    {{! @glint-expect-error: Incorrect type }}
                    @isExpanded={{get this.expandedDefinitions definition.name}}
                  />
                  <div
                    class="definition-item__name paragraph-200--bold truncate"
                  >
                    {{t category}}
                  </div>
                </button>

                {{#if (get this.expandedDefinitions definition.name)}}
                  <div class="definition-item__expanded-content">
                    <SettingsMappingDefinitionsDefinitionContent
                      @description={{definition.description}}
                      @points={{definition.points}}
                    />
                  </div>
                {{/if}}
              </div>
            {{/each-in}}
          </div>
        </div>
      </:content>
    </PrismDrawer>
  </template>
}
